import React, { useState, useEffect } from 'react';
import { ChevronLeftIcon } from '../../../../atoms/material-icons';
import { BQLink } from '../../../../atoms/bqlink';
import RangeSelector from '../../../../atoms/range-slider';
import { Line } from 'react-chartjs-2';
import './sectoral-indices-external-widget.m.css';
import EmptyState from '../empty-state';
import { widgetLink } from '../../../../../helpers/utils';
import { useQuery } from 'react-query';
import Loader from '../../../../atoms/icons/loader';

const SectoralIndicesExternalWidget = ({ widget = false }) => {
  const widgetParam = widget ? '?view=apps' : '';
  const [indicesData, setIndicesData] = useState([]);
  const [indicesDetails, setIndicesDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [allIndicesTab, setAllIndicesTab] = useState(false);
  const [bseIndicesTab, setBseIndicesTab] = useState(false);
  const [nseIndicesTab, setNseIndicesTab] = useState(false);
  const [gainingTab, setGainingTab] = useState(false);
  const [losingTab, setLosingTab] = useState(false);
  const [oneD, setOneD] = useState(false);
  const [oneW, setOneW] = useState(false);
  const [oneM, setOneM] = useState(false);
  const [oneYTD, setOneYTD] = useState(false);
  const [oneY, setOneY] = useState(false);
  const [threeY, setThreeY] = useState(false);
  const [toggle, setToggle] = useState(-1);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedDuration, setSelectedDuration] = useState('');
  const [selectedKey, setSelectedKey] = useState('');
  const [selectedIndex, setSelectedIndex] = useState('');

  const getSectoralIndicesOverallData = async (duration = '1D', tab = 'all') => {
    const response = await fetch(
      `/feapi/markets/sectoral-indice-external/indices/sectoral-indices/${duration}/${tab}`,
    );
    return response.json();
  };

  const getSectoralIndicesDetailsData = async (key = '1', duration = '1D') => {
    const response = await fetch(`/feapi/markets/indices/${key}/details/${duration}`);
    return response.json();
  };

  const resultSectoralIndicesOverallData = useQuery(
    'getSectoralIndicesOverallData',
    () => getSectoralIndicesOverallData(selectedDuration, selectedIndex),
    {
      refetchInterval: 30000,
      refetchIntervalInBackground: true,
      onSuccess: (data) => {
        setIndicesData(data.data);
      },
      enabled: !!selectedDuration && !!selectedIndex,
    },
  );

  const resultSectoralIndicesOverallDetails = useQuery(
    'getIndianIndicesSectoralExternalDetailsData',
    () => getSectoralIndicesDetailsData(selectedKey, selectedDuration),
    {
      refetchInterval: 30000,
      refetchIntervalInBackground: true,
      onSuccess: (data) => {
        setIndicesDetails(data.data);
      },
      enabled: !!selectedKey && !!selectedDuration,
    },
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const getSectoralIndicesOverallData = async () => {
      const response = await fetch(
        '/feapi/markets/sectoral-indice-external/indices/sectoral-indices/1D/all',
      );
      const data = await response.json();
      setIndicesData(data?.data);
    };

    const getSectoralIndicesDetailsData = async () => {
      const response = await fetch('/feapi/markets/indices/5/details/1D');
      const data = await response.json();
      setIndicesDetails(data.data);
    };

    getSectoralIndicesOverallData();
    getSectoralIndicesDetailsData();
  }, []);

  useEffect(() => {
    setSelectedDuration('1D');
    setSelectedKey('5');
    setSelectedIndex('all');
    if (resultSectoralIndicesOverallData?.isSuccess) {
      setIndicesData(resultSectoralIndicesOverallData?.data?.data);
    }

    if (resultSectoralIndicesOverallDetails?.isSuccess) {
      setIndicesDetails(resultSectoralIndicesOverallDetails?.data?.data);
    }

    setOneD(true);
    setOneW(false);
    setOneM(false);
    setOneYTD(false);
    setOneY(false);
    setThreeY(false);
    setAllIndicesTab(true);
    setBseIndicesTab(false);
    setNseIndicesTab(false);
    setGainingTab(false);
    setLosingTab(false);
    setToggle(0);
  }, []);

  const indexChangeHandler = async (tab) => {
    setLoading(true);
    const response = await fetch(
      `/feapi/markets/sectoral-indice-external/indices/sectoral-indices/1D/${tab}`,
    );
    const data = await response.json();
    setIndicesData(data?.data);
    setSelectedDuration('1D');
    setLoading(false);
    setOneD(true);
    setOneW(false);
    setOneM(false);
    setOneYTD(false);
    setOneY(false);
    setThreeY(false);
    setToggle(0);
  };

  const durationHandler = async (duration, tab) => {
    setLoading(true);
    const response = await fetch(
      `/feapi/markets/sectoral-indice-external/indices/sectoral-indices/${duration}/${tab}`,
    );
    const data = await response.json();
    setIndicesData(data.data);
    setLoading(false);
  };

  const detailsHandler = async (duration, tab) => {
    setDetailsLoading(true);
    const responseIndices = await fetch(
      `/feapi/markets/sectoral-indice-external/indices/sectoral-indices/${duration}/${tab}`,
    );
    const dataIndices = await responseIndices.json();
    const defaultKey = dataIndices?.data[0]?.id;
    setSelectedKey(defaultKey);
    setSelectedDuration(duration);
    const response = await fetch(`/feapi/markets/indices/${defaultKey}/details/${'1D'}`);
    const data = await response.json();
    setIndicesDetails(data.data);
    setDetailsLoading(false);
  };

  const options = {
    animation: false,
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
    elements: {
      point: {
        radius: 0,
      },
    },
    scales: {
      x: {
        ticks: {
          display: false,
        },
        grid: {
          drawBorder: false,
          display: false,
        },
      },
      y: {
        ticks: {
          display: false,
        },
        grid: {
          drawBorder: false,
          display: false,
        },
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
  };

  const requiredLabels =
    indicesDetails &&
    indicesDetails?.graph?.map((value) => {
      return value[0];
    });

  const dataGraph = {
    labels: requiredLabels,
    datasets: [
      {
        fill: true,
        label: '',
        data:
          indicesDetails &&
          indicesDetails?.graph?.map((value) => {
            return value[1];
          }),
        borderColor: '#14c588',
        backgroundColor: '#14c588',
      },
    ],
  };

  const fetchIndiceOverallDetailsHandler = async (key, duration, index) => {
    try {
      setDetailsLoading(true);
      const response = await fetch(`/feapi/markets/indices/${key}/details/${duration}`);
      const data = await response.json();
      setIndicesDetails(data.data);
      setDetailsLoading(false);
      setToggle(index);
    } catch (error) {
      console.log('Error Fetching Indice details', error);
    }
  };

  const total = indicesDetails?.highlights?.declines + indicesDetails?.highlights?.advances;

  if (
    resultSectoralIndicesOverallData?.isLoading ||
    resultSectoralIndicesOverallDetails?.isLoading
  ) {
    <div>
      <Loader color={'#dadee1'} />
    </div>;
  }

  if (initialLoading) {
    return <div styleName="initial-loading-container"></div>;
  }

  return (
    <div styleName="overall-container">
      <div styleName="header-container">
        <div styleName="icon-text-container">
          <BQLink href={widgetLink(`/markets${widgetParam}`, widget)} styleName="header-btn">
            <ChevronLeftIcon color="#8F9DA5" size={'25px'} />
          </BQLink>
          <h1 styleName="header-text">Sectoral Indices</h1>
        </div>

        <div styleName="btn-container">
          <button
            styleName={`indice-btn ${allIndicesTab ? 'selected' : ''}`}
            onClick={() => {
              indexChangeHandler('all');
              setSelectedIndex('all');
              durationHandler('1D', 'all');
              detailsHandler('1D', 'all');
              setAllIndicesTab(true);
              setBseIndicesTab(false);
              setNseIndicesTab(false);
              setGainingTab(false);
              setLosingTab(false);
            }}
          >
            All Indices
          </button>
          <button
            styleName={`indice-btn ${bseIndicesTab ? 'selected' : ''}`}
            onClick={() => {
              indexChangeHandler('bse');
              setSelectedIndex('bse');
              durationHandler('1D', 'bse');
              detailsHandler('1D', 'bse');
              setAllIndicesTab(false);
              setBseIndicesTab(true);
              setNseIndicesTab(false);
              setGainingTab(false);
              setLosingTab(false);
            }}
          >
            BSE Sectoral
          </button>
          <button
            styleName={`indice-btn ${nseIndicesTab ? 'selected' : ''}`}
            onClick={() => {
              indexChangeHandler('nse');
              setSelectedIndex('nse');
              durationHandler('1D', 'nse');
              detailsHandler('1D', 'nse');
              setAllIndicesTab(false);
              setBseIndicesTab(false);
              setNseIndicesTab(true);
              setGainingTab(false);
              setLosingTab(false);
            }}
          >
            NSE Sectoral
          </button>
          <button
            styleName={`indice-btn ${gainingTab ? 'selected' : ''}`}
            onClick={() => {
              indexChangeHandler('gaining');
              setSelectedIndex('gaining');
              durationHandler('1D', 'gaining');
              detailsHandler('1D', 'gaining');
              setAllIndicesTab(false);
              setBseIndicesTab(false);
              setNseIndicesTab(false);
              setGainingTab(true);
              setLosingTab(false);
            }}
          >
            Gaining Sectors
          </button>
          <button
            styleName={`indice-btn ${losingTab ? 'selected' : ''}`}
            onClick={() => {
              indexChangeHandler('losing');
              setSelectedIndex('losing');
              durationHandler('1D', 'losing');
              detailsHandler('1D', 'losing');
              setAllIndicesTab(false);
              setBseIndicesTab(false);
              setNseIndicesTab(false);
              setGainingTab(false);
              setLosingTab(true);
            }}
          >
            Losing Sectors
          </button>
        </div>
      </div>

      <div styleName="content-container">
        <div styleName="table-container">
          {indicesData && indicesData?.length > 0 ? (
            <>
              <div styleName="duration-btn-container">
                <button
                  styleName={`duration-btn ${oneD ? 'duration-selected' : ' '} `}
                  onClick={() => {
                    durationHandler('1D', selectedIndex);
                    setSelectedDuration('1D');
                    fetchIndiceOverallDetailsHandler(indicesData && indicesData[0].id, '1D', 0);
                    setSelectedKey(indicesData[0]?.id);
                    setOneD(true);
                    setOneW(false);
                    setOneM(false);
                    setOneYTD(false);
                    setOneY(false);
                    setThreeY(false);
                  }}
                >
                  1D
                </button>
                <button
                  styleName={`duration-btn ${oneW ? 'duration-selected' : ' '} `}
                  onClick={() => {
                    durationHandler('1W', selectedIndex);
                    setSelectedDuration('1W');
                    fetchIndiceOverallDetailsHandler(indicesData && indicesData[0].id, '1W', 0);
                    setSelectedKey(indicesData[0]?.id);
                    setOneD(false);
                    setOneW(true);
                    setOneM(false);
                    setOneYTD(false);
                    setOneY(false);
                    setThreeY(false);
                  }}
                >
                  1W
                </button>
                <button
                  styleName={`duration-btn ${oneM ? 'duration-selected' : ' '} `}
                  onClick={() => {
                    durationHandler('1M', selectedIndex);
                    setSelectedDuration('1M');
                    fetchIndiceOverallDetailsHandler(indicesData && indicesData[0].id, '1M', 0);
                    setSelectedKey(indicesData[0]?.id);
                    setOneD(false);
                    setOneW(false);
                    setOneM(true);
                    setOneYTD(false);
                    setOneY(false);
                    setThreeY(false);
                  }}
                >
                  1M
                </button>
                <button
                  styleName={`duration-btn ${oneYTD ? 'duration-selected' : ' '} `}
                  onClick={() => {
                    durationHandler('YTD', selectedIndex);
                    setSelectedDuration('YTD');
                    fetchIndiceOverallDetailsHandler(indicesData && indicesData[0].id, 'YTD', 0);
                    setSelectedKey(indicesData[0]?.id);
                    setOneD(false);
                    setOneW(false);
                    setOneM(false);
                    setOneYTD(true);
                    setOneY(false);
                    setThreeY(false);
                  }}
                >
                  YTD
                </button>
                <button
                  styleName={`duration-btn ${oneY ? 'duration-selected' : ' '} `}
                  onClick={() => {
                    durationHandler('1Y', selectedIndex);
                    setSelectedDuration('1Y');
                    fetchIndiceOverallDetailsHandler(indicesData && indicesData[0].id, '1Y', 0);
                    setSelectedKey(indicesData[0]?.id);
                    setOneD(false);
                    setOneW(false);
                    setOneM(false);
                    setOneYTD(false);
                    setOneY(true);
                    setThreeY(false);
                  }}
                >
                  1Y
                </button>
                <button
                  styleName={`duration-btn ${threeY ? 'duration-selected' : ' '} `}
                  onClick={() => {
                    durationHandler('3Y', selectedIndex);
                    setSelectedDuration('3Y');
                    fetchIndiceOverallDetailsHandler(indicesData && indicesData[0].id, '3Y', 0);
                    setSelectedKey(indicesData[0]?.id);
                    setOneD(false);
                    setOneW(false);
                    setOneM(false);
                    setOneYTD(false);
                    setOneY(false);
                    setThreeY(true);
                  }}
                >
                  3Y
                </button>
              </div>
              {loading ? (
                <div styleName="no-data-container-ext">
                  <div styleName="no-data-container-int">
                    <Loader color={'#dadee1'} />
                  </div>
                </div>
              ) : (
                <>
                  {' '}
                  <div styleName="table-body-container">
                    <table styleName="main-table">
                      <thead styleName="table-header">
                        <tr>
                          <th styleName="column-title">Indices</th>
                          <th styleName="column-title-alt">Price</th>
                          <th styleName="column-title-alt">Change</th>
                          <th styleName="column-title-alt">Change %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {indicesData &&
                          indicesData?.map((indiceValue, index) => (
                            <tr
                              key={indiceValue?.id}
                              styleName={`data-row ${index === toggle ? 'selected-filter' : ''}`}
                              onClick={() => {
                                setSelectedKey(indiceValue.id);
                                fetchIndiceOverallDetailsHandler(
                                  indiceValue.id,
                                  selectedDuration,
                                  index,
                                );
                              }}
                            >
                              <td styleName="name">{indiceValue.sname}</td>
                              <td styleName="current-price">
                                {indiceValue['price-movement']['current-price']}
                              </td>
                              <td styleName="change-values">
                                {String(indiceValue['price-movement']?.chg).includes('-') ? (
                                  <span styleName="negative-value">
                                    {indiceValue['price-movement']?.chg
                                      ? indiceValue['price-movement']?.chg
                                      : 0}
                                  </span>
                                ) : (
                                  <span styleName="positive-value">
                                    {indiceValue['price-movement']?.chg
                                      ? indiceValue['price-movement']?.chg
                                      : 0}
                                  </span>
                                )}
                              </td>
                              <td styleName="change-values">
                                {String(indiceValue['price-movement']?.chg).includes('-') ? (
                                  <span styleName="negative-value">
                                    {`${
                                      indiceValue['price-movement']?.chgp
                                        ? indiceValue['price-movement']?.chgp
                                        : 0
                                    } %`}
                                  </span>
                                ) : (
                                  <span styleName="positive-value">
                                    {`${
                                      indiceValue['price-movement']?.chgp
                                        ? indiceValue['price-movement']?.chgp
                                        : 0
                                    } %`}
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                  <div styleName={`end-container ${widget ? 'widget-mode' : ''}`}></div>
                </>
              )}
            </>
          ) : (
            <div styleName="no-data-container-ext">
              <div styleName="no-data-container-int">
                <EmptyState />
              </div>
            </div>
          )}
        </div>
        {detailsLoading ? (
          <div styleName="loading-ext-alt">
            <div styleName="no-data-container-int">
              <Loader color={'#dadee1'} />
            </div>
          </div>
        ) : (
          <div styleName="graph-data-container">
            {indicesDetails && Object.keys(indicesDetails).length > 0 ? (
              <>
                <div styleName="stat-slider-container">
                  <div styleName="stats-container">
                    <div styleName="top-gainer-container">
                      <p styleName="top-stat-title">Top Gainer</p>
                      {indicesDetails?.highlights?.topgainer?.name ? (
                        <BQLink
                          href={widgetLink(
                            `/stock/${
                              indicesDetails?.highlights?.topgainer?.sid
                            }/${indicesDetails?.highlights?.topgainer?.name
                              ?.toLowerCase()
                              .trim()
                              .replaceAll(' ', '-')
                              .split('-')
                              .filter((val) => val !== '')
                              .map((val) => val.replaceAll('.', ''))
                              .join('-')}${widgetParam}`,
                            widget,
                          )}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <p styleName="top-stat-name">
                            {indicesDetails?.highlights?.topgainer?.name}
                          </p>
                        </BQLink>
                      ) : (
                        <p styleName="top-stat-name">No Gainers</p>
                      )}

                      {indicesDetails?.highlights?.topgainer?.chgp ? (
                        <p styleName="top-stat-positive">
                          &#9650;+{indicesDetails?.highlights?.topgainer?.chgp}%
                        </p>
                      ) : (
                        <p styleName="top-stat-positive">-</p>
                      )}
                    </div>
                    <div styleName="top-loser-container">
                      <p styleName="top-stat-title">Top Loser</p>
                      {indicesDetails?.highlights?.toploser?.name ? (
                        <BQLink
                          href={widgetLink(
                            `/stock/${
                              indicesDetails?.highlights?.toploser?.sid
                            }/${indicesDetails?.highlights?.toploser?.name
                              ?.toLowerCase()
                              .trim()
                              .replaceAll(' ', '-')
                              .split('-')
                              .filter((val) => val !== '')
                              .map((val) => val.replaceAll('.', ''))
                              .join('-')}${widgetParam}`,
                            widget,
                          )}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <p styleName="top-stat-name">
                            {indicesDetails?.highlights?.toploser?.name}
                          </p>
                        </BQLink>
                      ) : (
                        <p styleName="top-stat-name">No Losers</p>
                      )}
                      {indicesDetails?.highlights?.toploser?.chgp ? (
                        <p styleName="top-stat-negative">
                          &#9660;{indicesDetails?.highlights?.toploser?.chgp}%
                        </p>
                      ) : (
                        <p styleName="top-stat-negative">-</p>
                      )}
                    </div>
                  </div>
                  <div styleName="range-slider-container">
                    <RangeSelector
                      min={indicesDetails['high-low'] && indicesDetails['high-low']['52-week-low']}
                      max={indicesDetails['high-low'] && indicesDetails['high-low']['52-week-high']}
                      currentValue={indicesDetails && indicesDetails['high-low']?.currentprice}
                      label=""
                      labelOne="52 Week Low"
                      labelTwo="52 Week High"
                    />
                    <RangeSelector
                      min={indicesDetails['high-low'] && indicesDetails['high-low']?.low}
                      max={indicesDetails['high-low'] && indicesDetails['high-low']?.high}
                      currentValue={indicesDetails && indicesDetails['high-low']?.currentprice}
                      label=""
                    />
                  </div>
                </div>
                <div styleName="graph-container">
                  {indicesDetails?.graph?.length === 0 ? (
                    <div styleName="no-data-container-ext-graph">
                      <div styleName="no-data-container-int">
                        <EmptyState />
                      </div>
                    </div>
                  ) : (
                    <Line options={options} data={dataGraph} width={'100%'} height={'60%'} />
                  )}
                </div>
                <div styleName="advances-stats-container">
                  <div styleName="advances-decline-overall-container">
                    {indicesDetails?.highlights?.advances ||
                    indicesDetails?.highlights?.declines ? (
                      <>
                        <div styleName="adv-dec-details">
                          <span>{indicesDetails?.highlights?.advances}</span>
                          <span>{indicesDetails?.highlights?.declines}</span>
                        </div>
                        <div styleName="adv-dec-container">
                          <div
                            styleName="adv-bar"
                            style={{
                              width: `${(indicesDetails?.highlights?.advances / total) * 100}%`,
                            }}
                          />
                        </div>
                        <div styleName="adv-dec-titles">
                          <span>Advances</span>
                          <span>Declines</span>
                        </div>
                      </>
                    ) : (
                      <div styleName="no-advances-msg">
                        No Advances and Declines Data Available.
                      </div>
                    )}
                  </div>
                  <div styleName="bottom-stats-container">
                    {indicesDetails['historical-returns'] &&
                      indicesDetails['historical-returns']?.map((value, index) => (
                        <div key={index} styleName="label-stat-container">
                          <span styleName="value-label">{value.label}</span>

                          {String(value.change).includes('-') ? (
                            <span styleName="value-stat-negative">-{Math.abs(value.change)}%</span>
                          ) : (
                            <span styleName="value-stat-positive">{value.change}%</span>
                          )}
                        </div>
                      ))}
                  </div>
                </div>
              </>
            ) : (
              <div styleName="no-data-container-ext-alt">
                <div styleName="no-data-container-int">
                  <EmptyState />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SectoralIndicesExternalWidget;
